import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { useAuth } from '../../../../contexts/AuthContext';
import { EmailEditor } from './EmailEditor';
import { templateByKey } from './emailTemplate';
import { Card, EmptyState, Status } from './ui';

/**
 * Campaigns, as mockup v0.5 lays them out (design v0.6 decisions 14, 15, 22): Draft / Active /
 * Archived, one Lever job per campaign, up to 3 messages, the FINN-branded email editor.
 * A draft can be written here to try the flow, but nothing is saved or sent yet: there are no
 * recipients, no Lever postings and no Gmail send path. The page says so plainly.
 */
type CampTab = 'draft' | 'active' | 'archived';
const MAX_TOUCHES = 3;

interface Message {
  id: number;
  subject: string;
  templateKey: string;
}
interface Draft {
  id: number;
  name: string;
  messages: Message[];
}

let nextId = 1;
const newMessage = (templateKey = 'rolefit'): Message => ({ id: nextId++, subject: templateByKey(templateKey).subject, templateKey });

const STATUSES: [string, string][] = [
  ['Interested', 'Clicked Interested in the email. Action shows “Waiting for candidate to confirm” until they confirm in their portal, then “Opportunity created in Lever” with a link.'],
  ['Not interested', 'Clicked Not right now.'],
  ['Opted out', 'Asked not to hear from us again. Removed from every campaign.'],
  ['No reaction', 'Has not clicked anything yet.'],
];

export function CampaignsTab() {
  const { user } = useAuth();
  const signer = user?.displayName || '';
  const [tab, setTab] = useState<CampTab>('draft');
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [open, setOpen] = useState<number | null>(null);

  const patch = (id: number, fn: (d: Draft) => Draft) => setDrafts((all) => all.map((d) => (d.id === id ? fn(d) : d)));
  const counts: Record<CampTab, number> = { draft: drafts.length, active: 0, archived: 0 };

  return (
    <div className="space-y-5">
      <div role="tablist" aria-label="Campaign status" className="flex gap-1">
        {(['draft', 'active', 'archived'] as const).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={
              tab === t
                ? 'rounded bg-zinc-900 px-4 py-2 text-sm font-medium text-white'
                : 'rounded px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100'
            }
          >
            {t === 'draft' ? 'Draft' : t === 'active' ? 'Active' : 'Archived'}{' '}
            <span className={tab === t ? 'text-zinc-300' : 'text-zinc-400'}>{counts[t]}</span>
          </button>
        ))}
      </div>

      {tab === 'draft' && (
        <>
          <p role="note" className="rounded border border-solid border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Drafts are not saved yet and nothing can be sent: they disappear when you leave this page. Use them to try
            the editor and the templates.
          </p>

          {drafts.map((d) => {
            const isOpen = open === d.id;
            return (
              <section key={d.id} className="rounded-lg border border-solid border-pearl bg-white">
                <div className="flex items-center gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : d.id)}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Collapse draft' : 'Expand draft'}
                    className="text-zinc-500"
                  >
                    {isOpen ? '▼' : '▶'}
                  </button>
                  <input
                    value={d.name}
                    onChange={(e) => patch(d.id, (x) => ({ ...x, name: e.target.value }))}
                    aria-label="Campaign name"
                    className="min-w-0 flex-1 rounded border border-solid border-transparent px-2 py-1 text-base font-semibold hover:border-zinc-200"
                  />
                  <span className="text-sm text-zinc-500">
                    <b className="text-zinc-900">0 of 0</b> eligible
                  </span>
                  <Status tone="preview">Draft</Status>
                </div>

                {isOpen && (
                  <div className="space-y-4 border-t border-solid border-pearl p-4">
                    <div className="rounded border border-solid border-zinc-200 bg-zinc-50 p-3 text-sm">
                      <p className="font-medium text-zinc-900">0 of 0 will receive this</p>
                      <p className="text-zinc-600">
                        Select people in Talent Search to add them. The draft will show who receives it and who is held
                        back, and why (only Scope A, in a Lever process, no valid email). Nothing is ever sent to someone
                        held back.
                      </p>
                    </div>

                    <div className="text-sm">
                      <label className="mb-1 block font-medium text-zinc-800" htmlFor={`posting-${d.id}`}>
                        Job posting this campaign is for
                      </label>
                      <select id={`posting-${d.id}`} disabled className="w-full rounded border border-solid border-zinc-200 bg-zinc-50 px-3 py-2 text-zinc-400">
                        <option>Lever job postings appear here once Lever is connected</option>
                      </select>
                      <p className="mt-1 text-xs text-zinc-500">
                        The Interested button carries this posting. When a candidate confirms, an opportunity is created for
                        them on it in Lever; the owner is the one set on the posting.
                      </p>
                    </div>

                    {d.messages.map((m, i) => (
                      <div key={m.id} className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-xs text-white">{i + 1}</span>
                          <span className="font-medium text-zinc-900">Message {i + 1}</span>
                          <span className="text-zinc-500">· {i === 0 ? 'sent when the campaign starts' : 'follow-up, if there is no reaction'}</span>
                          {i > 0 && (
                            <button
                              type="button"
                              onClick={() => patch(d.id, (x) => ({ ...x, messages: x.messages.filter((y) => y.id !== m.id) }))}
                              className="ml-auto text-sm text-zinc-500 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-zinc-500">From {signer || 'you'} &lt;{user?.email || 'your FINN address'}&gt;, through your Gmail</p>
                        <input
                          value={m.subject}
                          onChange={(e) =>
                            patch(d.id, (x) => ({ ...x, messages: x.messages.map((y) => (y.id === m.id ? { ...y, subject: e.target.value } : y)) }))
                          }
                          aria-label={`Subject of message ${i + 1}`}
                          placeholder="Subject"
                          className="w-full rounded border border-solid border-zinc-300 px-3 py-2 text-sm"
                        />
                        <EmailEditor
                          key={`${m.id}-${m.templateKey}`}
                          meta={templateByKey(m.templateKey)}
                          subject={m.subject}
                          signerName={signer}
                          onPickTemplate={(key) =>
                            patch(d.id, (x) => ({
                              ...x,
                              messages: x.messages.map((y) => (y.id === m.id ? { ...y, templateKey: key, subject: templateByKey(key).subject } : y)),
                            }))
                          }
                        />
                      </div>
                    ))}

                    <div className="flex flex-wrap items-center gap-3">
                      {d.messages.length < MAX_TOUCHES && (
                        <Button size="sm" variant="secondary" onClick={() => patch(d.id, (x) => ({ ...x, messages: [...x.messages, newMessage(x.messages[0]?.templateKey)] }))}>
                          + Add follow-up message
                        </Button>
                      )}
                      <span className="text-sm text-zinc-500">
                        {d.messages.length} of {MAX_TOUCHES} messages used
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 border-t border-solid border-pearl pt-4">
                      <Button size="sm" variant="ghost" onClick={() => setDrafts((all) => all.filter((x) => x.id !== d.id))}>
                        Discard
                      </Button>
                      <span className="ml-auto text-xs text-zinc-500">Sending is not built yet</span>
                      <Button size="sm" disabled>
                        Send now →
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            );
          })}

          {drafts.length === 0 && (
            <EmptyState title="No drafts">
              A draft starts when you select people in Talent Search and choose “Create campaign”. You can also start an
              empty one to try the editor.
            </EmptyState>
          )}

          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              const id = nextId++;
              setDrafts((all) => [{ id, name: 'New campaign', messages: [newMessage()] }, ...all]);
              setOpen(id);
            }}
          >
            + New draft campaign
          </Button>
        </>
      )}

      {tab === 'active' && (
        <>
          <EmptyState title="No active campaigns">
            Once a campaign is sent it shows here: recipients, click rate, expressed interest, in process and opt-outs, and
            one row per person.
          </EmptyState>
          <Card title="What each person’s status will mean">
            <dl className="space-y-2 text-sm">
              {STATUSES.map(([s, what]) => (
                <div key={s}>
                  <dt className="font-medium text-zinc-900">{s}</dt>
                  <dd className="text-zinc-600">{what}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-xs text-zinc-500">
              A Lead who enters the process is anonymized here; the row links to Lever instead. Follow-ups can be added
              until 3 messages are used.
            </p>
          </Card>
        </>
      )}

      {tab === 'archived' && (
        <EmptyState title="No archived campaigns">
          Campaigns move here automatically 30 days after they stop being active, with their final numbers and who was
          hired.
        </EmptyState>
      )}
    </div>
  );
}
